import { useCallback, useEffect, useMemo, useState } from 'react';
import { CopyField, FilterCard, MetricCard, MetricGrid, SectionCard } from '@/components/DataList';
import { ImagePicker, mediaUrl } from '@/components/ImagePicker';
import { ListTable, ScreenFrame } from '@/components/Screen';
import { Icon } from '@/components/icons';
import { admin } from '@/api/endpoints';
import { apiGet, apiPost, errorMessage, pickList, type JsonMap } from '@/api/client';
import { useAsync } from '@/hooks/useAsync';
import {
  Button,
  Empty,
  ErrorBox,
  FlashMessage,
  Form,
  FormActions,
  Input,
  Loading,
  Modal,
  Money,
  ProSearch,
  Select,
  StatusBadge,
} from '@/components/ui';
import { required, str } from '@/utils/format';
import { PROVIDER_TYPE_OPTIONS } from '@/utils/labels';

type CatalogData = {
  products: JsonMap[];
  categories: JsonMap[];
  providers: JsonMap[];
};

const PRODUCT_TYPE_LABELS: Record<string, string> = {
  package: 'ثابت',
  amount: 'كمية حرة',
  specific_package: 'كميات محددة',
};

function useProductsCatalog() {
  return useAsync(
    () => admin.productsLocal(),
    (p): CatalogData => {
      const root = p.data && typeof p.data === 'object' ? (p.data as JsonMap) : p;
      return {
        products: pickList(p, 'products', 'items', 'data'),
        categories: pickList(p, 'categories', 'items', 'data'),
        providers: Array.isArray(root.providers) ? (root.providers as JsonMap[]) : [],
      };
    },
  );
}

function categoryPath(c: JsonMap): string {
  return str(c.category_path ?? c.name);
}

function categoryKind(c: JsonMap): string {
  return str(c.category_kind, Number(c.parent_id) > 0 ? 'sub' : 'main');
}

function productImageSrc(p: JsonMap): string {
  return str(p.image_url ?? p.image ?? p.category_img);
}

function isProductVisible(p: JsonMap): boolean {
  return Number(p.is_active ?? 1) === 1 && Number(p.available ?? 1) === 1;
}

function TableThumb({ src, label }: { src: unknown; label?: string }) {
  const url = mediaUrl(src);
  return (
    <div className="image-thumb">
      {url ? <img src={url} alt={label ?? ''} /> : <Icon name="package" size={20} />}
    </div>
  );
}

function isManualProduct(p: JsonMap): boolean {
  const providerId = Number(p.provider_id ?? 0);
  const providerType = str(p.provider_type, '');
  return providerId === 0 || providerType === 'manual';
}

function productTypeLabel(p: JsonMap): string {
  const key = str(p.product_type, 'package');
  return PRODUCT_TYPE_LABELS[key] ?? key;
}

function priceSuffix(p: JsonMap): string {
  const cur = str(p.pricing_currency, 'USD').toUpperCase();
  return cur === 'SYP' ? 'ل.س' : '$';
}

function parseQtyState(p: JsonMap | null): {
  quantityMode: 'fixed' | 'range' | 'options';
  qtyMin: string;
  qtyMax: string;
  qtyRows: { qty: string; price: string }[];
} {
  let decoded: unknown = p?.qty_values_json;
  if (typeof decoded === 'string' && decoded.trim()) {
    try {
      decoded = JSON.parse(decoded);
    } catch {
      decoded = null;
    }
  }
  if (Array.isArray(decoded) && decoded.length > 0) {
    if (typeof decoded[0] === 'object' && decoded[0] !== null && 'qty' in (decoded[0] as JsonMap)) {
      return {
        quantityMode: 'options',
        qtyMin: '',
        qtyMax: '',
        qtyRows: decoded.map((row) => {
          const r = row as JsonMap;
          return { qty: str(r.qty), price: str(r.price) };
        }),
      };
    }
  }
  if (decoded && typeof decoded === 'object' && !Array.isArray(decoded)) {
    const obj = decoded as JsonMap;
    if (obj.min != null && obj.max != null) {
      return {
        quantityMode: 'range',
        qtyMin: str(obj.min),
        qtyMax: str(obj.max),
        qtyRows: [{ qty: '', price: '' }],
      };
    }
  }
  const pt = str(p?.product_type, 'package');
  if (pt === 'amount') {
    return { quantityMode: 'range', qtyMin: '', qtyMax: '', qtyRows: [{ qty: '', price: '' }] };
  }
  if (pt === 'specific_package') {
    return { quantityMode: 'options', qtyMin: '', qtyMax: '', qtyRows: [{ qty: '1', price: str(p?.sell_price ?? '') }] };
  }
  return { quantityMode: 'fixed', qtyMin: '', qtyMax: '', qtyRows: [{ qty: '', price: '' }] };
}

function targetLabelFromParams(p: JsonMap | null): string {
  const raw = p?.params_json;
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const arr = JSON.parse(raw) as unknown;
      if (Array.isArray(arr) && arr[0]) return str(arr[0]);
    } catch {
      return str(raw);
    }
  }
  if (Array.isArray(raw) && raw[0]) return str(raw[0]);
  return 'معرّف اللاعب / الحساب';
}

type ProductForm = {
  id: string;
  sourceKind: 'manual' | 'api';
  name: string;
  sellPrice: string;
  categoryId: string;
  imageUrl: string;
  targetLabel: string;
  imageShape: string;
  sortOrder: string;
  isActive: string;
  available: string;
  quantityMode: 'fixed' | 'range' | 'options';
  qtyMin: string;
  qtyMax: string;
  qtyRows: { qty: string; price: string }[];
  providerId: string;
  externalProductId: string;
};

function emptyProductForm(categories: JsonMap[]): ProductForm {
  return {
    id: '',
    sourceKind: 'manual',
    name: '',
    sellPrice: '',
    categoryId: categories[0] ? str(categories[0].id) : '',
    imageUrl: '',
    targetLabel: 'معرّف اللاعب / الحساب',
    imageShape: 'square',
    sortOrder: '0',
    isActive: '1',
    available: '1',
    quantityMode: 'fixed',
    qtyMin: '',
    qtyMax: '',
    qtyRows: [{ qty: '', price: '' }],
    providerId: '',
    externalProductId: '',
  };
}

function productToForm(p: JsonMap, categories: JsonMap[]): ProductForm {
  const qty = parseQtyState(p);
  return {
    id: str(p.id),
    sourceKind: isManualProduct(p) ? 'manual' : 'api',
    name: str(p.name),
    sellPrice: str(p.sell_price ?? p.price),
    categoryId: str(p.category_id ?? categories[0]?.id ?? ''),
    imageUrl: str(p.category_img ?? p.image_url),
    targetLabel: targetLabelFromParams(p),
    imageShape: str(p.image_shape, 'square'),
    sortOrder: str(p.sort_order ?? '0'),
    isActive: Number(p.is_active ?? 1) === 1 ? '1' : '0',
    available: Number(p.available ?? 1) === 1 ? '1' : '0',
    quantityMode: qty.quantityMode,
    qtyMin: qty.qtyMin,
    qtyMax: qty.qtyMax,
    qtyRows: qty.qtyRows.length ? qty.qtyRows : [{ qty: '', price: '' }],
    providerId: str(p.provider_id),
    externalProductId: str(p.external_id),
  };
}

function buildManualBody(form: ProductForm): JsonMap {
  const body: JsonMap = {
    name: form.name,
    sell_price: Number(form.sellPrice) || 0,
    category_id: Number(form.categoryId) || 0,
    category_img: form.imageUrl,
    image_url: form.imageUrl,
    target_label: form.targetLabel,
    image_shape: form.imageShape,
    sort_order: Number(form.sortOrder) || 0,
    is_active: form.isActive === '1' ? 1 : 0,
    available: form.available === '1' ? 1 : 0,
    quantity_mode: form.quantityMode === 'fixed' ? 'fixed' : form.quantityMode === 'range' ? 'range' : 'options',
    product_type: form.quantityMode === 'range' ? 'amount' : form.quantityMode === 'options' ? 'specific_package' : 'package',
  };
  if (form.id) body.id = form.id;
  if (form.quantityMode === 'range') {
    body.qty_min = Number(form.qtyMin);
    body.qty_max = Number(form.qtyMax);
  }
  if (form.quantityMode === 'options') {
    body.qty_prices = form.qtyRows
      .filter((r) => r.qty.trim() && r.price.trim())
      .map((r) => ({ qty: Number(r.qty), price: Number(r.price) }));
  }
  return body;
}

function ProductEditorModal({
  open,
  form,
  categories,
  providers,
  onClose,
  onChange,
  onSaved,
}: {
  open: boolean;
  form: ProductForm;
  categories: JsonMap[];
  providers: JsonMap[];
  onClose: () => void;
  onChange: (next: ProductForm) => void;
  onSaved: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const isNew = !form.id;
  const isManual = form.sourceKind === 'manual' || isNew;

  async function save() {
    setMsg('');

    if (isNew && form.sourceKind === 'api') {
      const miss = required(
        { providerId: form.providerId, externalProductId: form.externalProductId, name: form.name },
        ['providerId', 'externalProductId', 'name'],
      );
      if (miss) {
        setMsg(miss);
        return;
      }
      setBusy(true);
      const res = await admin.apiProductSave({
        provider_id: Number(form.providerId),
        external_product_id: Number(form.externalProductId),
        name: form.name,
        category_id: Number(form.categoryId) || 0,
        category_img: form.imageUrl,
        sort_order: Number(form.sortOrder) || 0,
        is_active: form.isActive === '1' ? 1 : 0,
      });
      setBusy(false);
      if (!res.success) {
        setMsg(errorMessage(res));
        return;
      }
      onSaved();
      onClose();
      return;
    }

    if (form.sourceKind === 'manual') {
      const miss = required({ name: form.name, sellPrice: form.sellPrice }, ['name', 'sellPrice']);
      if (miss) {
        setMsg(miss);
        return;
      }
      if (form.quantityMode === 'range' && (!form.qtyMin.trim() || !form.qtyMax.trim())) {
        setMsg('أدخل حداً أدنى وأعلى صالحين للكمية الحرة.');
        return;
      }
      if (form.quantityMode === 'options') {
        const rows = form.qtyRows.filter((r) => r.qty.trim() && r.price.trim());
        if (!rows.length) {
          setMsg('أضف كمية محددة واحدة على الأقل.');
          return;
        }
      }
      setBusy(true);
      const res = await admin.manualSave(buildManualBody(form));
      setBusy(false);
      if (!res.success) {
        setMsg(errorMessage(res));
        return;
      }
      onSaved();
      onClose();
      return;
    }

    const body: JsonMap = {
      id: form.id,
      name: form.name,
      category_id: Number(form.categoryId) || 0,
      category_img: form.imageUrl,
      image_url: form.imageUrl,
      sort_order: Number(form.sortOrder) || 0,
      is_active: form.isActive === '1' ? 1 : 0,
      image_shape: form.imageShape,
    };
    setBusy(true);
    const res = await admin.productSave(body);
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    onSaved();
    onClose();
  }

  const apiProviders = providers.filter(
    (pr) => str(pr.provider_type) !== 'manual' && Number(pr.is_active ?? 1) === 1,
  );

  return (
    <Modal
      open={open}
      wide
      title={isNew ? 'إضافة منتج' : 'تعديل منتج'}
      subtitle={
        isManual
          ? 'منتج يدوي — السعر بالدولار.'
          : 'منتج API — عدّل بيانات العرض أو اربطه من شاشة الاستيراد.'
      }
      onClose={onClose}
    >
      <Form className="form-grid two-columns" onSubmit={() => void save()}>
        {isNew ? (
          <Select
            label="مصدر المنتج"
            value={form.sourceKind}
            onChange={(v) => onChange({ ...form, sourceKind: v as 'manual' | 'api' })}
            options={[
              { value: 'manual', label: 'يدوي' },
              { value: 'api', label: 'API' },
            ]}
          />
        ) : null}

        {isNew && form.sourceKind === 'api' ? (
          <>
            <Select
              label="المصدر"
              value={form.providerId}
              onChange={(v) => onChange({ ...form, providerId: v })}
              options={[
                { value: '', label: 'اختر المصدر' },
                ...apiProviders.map((pr) => ({
                  value: str(pr.id),
                  label: str(pr.provider_name, str(pr.id)),
                })),
              ]}
              required
            />
            <Input
              label="رقم المنتج في المصدر"
              value={form.externalProductId}
              onChange={(e) => onChange({ ...form, externalProductId: e.target.value.replace(/[^0-9]/g, '') })}
              inputMode="numeric"
              required
            />
          </>
        ) : null}

        <Input
          className="span-2"
          label="اسم المنتج"
          value={form.name}
          onChange={(e) => onChange({ ...form, name: e.target.value })}
          required
        />

        {(isManual || isNew) && form.sourceKind === 'manual' ? (
          <>
            <Select
              label="نوع المنتج"
              value={form.quantityMode}
              onChange={(v) =>
                onChange({ ...form, quantityMode: v as ProductForm['quantityMode'] })
              }
              options={[
                { value: 'fixed', label: 'ثابت بدون اختيار كمية' },
                { value: 'range', label: 'كمية حرة بين حد أدنى وأعلى' },
                { value: 'options', label: 'كميات محددة مسبقاً' },
              ]}
            />
            {form.quantityMode === 'fixed' || form.quantityMode === 'range' ? (
              <Input
                label={form.quantityMode === 'range' ? 'سعر الوحدة بالدولار' : 'سعر المنتج بالدولار'}
                value={form.sellPrice}
                onChange={(e) => onChange({ ...form, sellPrice: e.target.value })}
                inputMode="decimal"
                required
              />
            ) : null}
            {form.quantityMode === 'range' ? (
              <div className="form-grid two span-2">
                <Input
                  label="الحد الأدنى"
                  value={form.qtyMin}
                  onChange={(e) => onChange({ ...form, qtyMin: e.target.value })}
                  inputMode="decimal"
                />
                <Input
                  label="الحد الأعلى"
                  value={form.qtyMax}
                  onChange={(e) => onChange({ ...form, qtyMax: e.target.value })}
                  inputMode="decimal"
                />
              </div>
            ) : null}
            {form.quantityMode === 'options' ? (
              <div className="stack-gap span-2">
                {form.qtyRows.map((row, idx) => (
                  <div key={idx} className="form-grid two">
                    <Input
                      label="الكمية"
                      value={row.qty}
                      onChange={(e) => {
                        const qtyRows = [...form.qtyRows];
                        qtyRows[idx] = { ...row, qty: e.target.value };
                        onChange({ ...form, qtyRows });
                      }}
                      inputMode="decimal"
                    />
                    <Input
                      label="السعر USD"
                      value={row.price}
                      onChange={(e) => {
                        const qtyRows = [...form.qtyRows];
                        qtyRows[idx] = { ...row, price: e.target.value };
                        onChange({ ...form, qtyRows });
                      }}
                      inputMode="decimal"
                    />
                  </div>
                ))}
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() =>
                    onChange({ ...form, qtyRows: [...form.qtyRows, { qty: '', price: '' }] })
                  }
                >
                  إضافة كمية
                </Button>
              </div>
            ) : null}
            <Input
              className="span-2"
              label="الحقل المطلوب من الزبون"
              value={form.targetLabel}
              onChange={(e) => onChange({ ...form, targetLabel: e.target.value })}
            />
          </>
        ) : (
          <Input
            label="سعر العرض"
            value={form.sellPrice}
            onChange={(e) => onChange({ ...form, sellPrice: e.target.value })}
            inputMode="decimal"
          />
        )}

        <Select
          label="القسم"
          value={form.categoryId}
          onChange={(v) => onChange({ ...form, categoryId: v })}
          options={[
            { value: '0', label: 'بدون قسم' },
            ...categories.map((c) => ({ value: str(c.id), label: categoryPath(c) })),
          ]}
        />

        <Input
          className="span-2"
          label="رابط الصورة"
          value={form.imageUrl}
          onChange={(e) => onChange({ ...form, imageUrl: e.target.value })}
          placeholder="https:// أو /uploads/..."
        />

        <Select
          label="شكل الصورة"
          value={form.imageShape}
          onChange={(v) => onChange({ ...form, imageShape: v })}
          options={[
            { value: 'square', label: 'مربّع' },
            { value: 'landscape', label: 'أفقي' },
            { value: 'portrait', label: 'عمودي' },
          ]}
        />

        <Input
          label="الترتيب"
          value={form.sortOrder}
          onChange={(e) => onChange({ ...form, sortOrder: e.target.value })}
          inputMode="numeric"
        />

        <Select
          label="الظهور"
          value={form.isActive}
          onChange={(v) => onChange({ ...form, isActive: v })}
          options={[
            { value: '1', label: 'ظاهر' },
            { value: '0', label: 'مخفي' },
          ]}
        />

        {(isManual || form.sourceKind === 'manual') && !isNew ? (
          <Select
            label="التوفر"
            value={form.available}
            onChange={(v) => onChange({ ...form, available: v })}
            options={[
              { value: '1', label: 'متوفر' },
              { value: '0', label: 'غير متوفر' },
            ]}
          />
        ) : null}

        {msg ? <p className="error">{msg}</p> : null}
        <FormActions className="modal-actions">
          <Button type="button" variant="secondary" onClick={onClose}>
            إلغاء
          </Button>
          <Button type="submit" disabled={busy}>
            حفظ المنتج
          </Button>
        </FormActions>
      </Form>
    </Modal>
  );
}

export function ProductsLocalScreen() {
  const state = useProductsCatalog();
  const [q, setQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editorOpen, setEditorOpen] = useState(false);
  const [productForm, setProductForm] = useState<ProductForm | null>(null);
  const [relinkOpen, setRelinkOpen] = useState(false);
  const [relinkTarget, setRelinkTarget] = useState<JsonMap | null>(null);
  const [relinkProviderId, setRelinkProviderId] = useState('');
  const [relinkExternalId, setRelinkExternalId] = useState('');
  const [flash, setFlash] = useState('');

  const products = state.data?.products ?? [];
  const categories = state.data?.categories ?? [];
  const providers = state.data?.providers ?? [];

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return products.filter((p) => {
      if (categoryFilter !== 'all' && str(p.category_id) !== categoryFilter) return false;
      const manual = isManualProduct(p);
      if (typeFilter === 'manual' && !manual) return false;
      if (typeFilter === 'api' && manual) return false;
      if (sourceFilter !== 'all' && str(p.provider_id ?? '0') !== sourceFilter) return false;
      const visible = isProductVisible(p);
      if (statusFilter === 'active' && !visible) return false;
      if (statusFilter === 'hidden' && visible) return false;
      if (!needle) return true;
      const hay = [str(p.name), str(p.category_title), str(p.provider_name), str(p.id)]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [products, q, categoryFilter, sourceFilter, typeFilter, statusFilter]);

  const manualCount = useMemo(() => filtered.filter(isManualProduct).length, [filtered]);
  const apiCount = useMemo(() => filtered.filter((p) => !isManualProduct(p)).length, [filtered]);
  const activeCount = useMemo(() => filtered.filter(isProductVisible).length, [filtered]);

  async function toggleVisibility(p: JsonMap) {
    const nextActive = Number(p.is_active ?? 1) === 1 ? 0 : 1;
    const res = await admin.productSave({ id: p.id, is_active: nextActive });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  function openCreate() {
    setProductForm(emptyProductForm(categories));
    setEditorOpen(true);
  }

  function openEdit(p: JsonMap) {
    setProductForm(productToForm(p, categories));
    setEditorOpen(true);
  }

  function openRelink(p: JsonMap) {
    setRelinkTarget(p);
    setRelinkProviderId(str(p.provider_id));
    setRelinkExternalId('');
    setRelinkOpen(true);
  }

  async function runRelink() {
    if (!relinkTarget) return;
    const res = await admin.productsRelink({
      id: relinkTarget.id,
      provider_id: Number(relinkProviderId),
      external_product_id: Number(relinkExternalId),
    });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    setFlash('تم تغيير ربط المنتج.');
    setRelinkOpen(false);
    state.reload();
  }

  async function remove(id: unknown) {
    if (!window.confirm('حذف المنتج نهائياً؟')) return;
    const res = await admin.productDelete({ id });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  const apiProviders = providers.filter((pr) => str(pr.provider_type) !== 'manual');

  return (
    <ScreenFrame
      mode="admin"
      icon="game"
      title="إدارة المنتجات"
      subtitle="إدارة المنتجات اليدوية ومنتجات API وتفاصيلها."
      actions={
        <>
          <Button type="button" variant="secondary" onClick={() => state.reload()}>
            <Icon name="refresh" size={18} /> تحديث
          </Button>
          <Button type="button" onClick={openCreate}>
            <Icon name="package" size={18} /> إضافة منتج
          </Button>
        </>
      }
    >
      {flash ? <p className="ok">{flash}</p> : null}

      <MetricGrid>
        <MetricCard title="المنتجات" value={String(filtered.length)} icon={<Icon name="package" size={21} />} />
        <MetricCard title="يدوية" value={String(manualCount)} icon={<Icon name="game" size={21} />} />
        <MetricCard title="API" value={String(apiCount)} icon={<Icon name="settings" size={21} />} />
        <MetricCard title="ظاهرة" value={String(activeCount)} icon={<Icon name="check" size={21} />} />
      </MetricGrid>

      <FilterCard title="تصفية المنتجات" icon={<Icon name="settings" size={24} />}>
        <ProSearch value={q} onChange={setQ} placeholder="بحث باسم المنتج أو القسم" />
        <Select
          label="القسم"
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[
            { value: 'all', label: 'كل الأقسام' },
            ...categories.map((c) => ({ value: str(c.id), label: categoryPath(c) })),
          ]}
        />
        <Select
          label="المصدر"
          value={sourceFilter}
          onChange={setSourceFilter}
          options={[
            { value: 'all', label: 'كل المصادر' },
            ...providers.map((pr) => ({
              value: str(pr.id),
              label: str(pr.provider_name, str(pr.id)),
            })),
          ]}
        />
        <Select
          label="النوع"
          value={typeFilter}
          onChange={setTypeFilter}
          options={[
            { value: 'all', label: 'يدوي + API' },
            { value: 'manual', label: 'يدوي فقط' },
            { value: 'api', label: 'API فقط' },
          ]}
        />
        <Select
          label="الحالة"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'active', label: 'ظاهر/متوفر' },
            { value: 'hidden', label: 'مخفي/غير متوفر' },
          ]}
        />
      </FilterCard>

      <SectionCard
        title={`المنتجات (${filtered.length})`}
        subtitle="استخدم «تعديل» لفتح نموذج المنتج."
        icon={<Icon name="package" size={24} />}
      >
        {filtered.length === 0 ? (
          <Empty title="لا منتجات مطابقة" hint="استخدم «تعديل» أو أضف منتجاً يدوياً." />
        ) : (
          <ListTable tableClassName="pro-table products-table">
            <thead>
              <tr>
                <th>الصورة</th>
                <th>المنتج</th>
                <th>القسم</th>
                <th>المصدر</th>
                <th>النوع</th>
                <th>السعر</th>
                <th>ترتيب</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => {
                const manual = isManualProduct(p);
                const active = Number(p.is_active ?? 1) === 1;
                const avail = Number(p.available ?? 1) === 1;
                return (
                  <tr key={str(p.id)}>
                    <td>
                      <TableThumb src={productImageSrc(p)} label={str(p.name)} />
                    </td>
                    <td>
                      <strong>{str(p.name)}</strong>
                      <small>#{str(p.id)}</small>
                    </td>
                    <td>{str(p.category_title ?? p.category_name, '—')}</td>
                    <td>{manual ? 'يدوي' : str(p.provider_name, 'API')}</td>
                    <td>{productTypeLabel(p)}</td>
                    <td>
                      <Money value={p.sell_price ?? p.price} suffix={priceSuffix(p)} />
                    </td>
                    <td>{str(p.sort_order ?? '0')}</td>
                    <td>
                      <div className="cell-stack">
                        <StatusBadge status={active ? 'active' : 'blocked'} />
                        <small>{avail ? 'متوفر' : 'غير متوفر'}</small>
                      </div>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button type="button" className="icon-btn" title="تعديل" onClick={() => openEdit(p)}>
                          <Icon name="edit" size={16} />
                        </button>
                        {!manual ? (
                          <button type="button" className="icon-btn" title="تغيير الربط" onClick={() => openRelink(p)}>
                            <Icon name="refresh" size={16} />
                          </button>
                        ) : null}
                        <button
                          type="button"
                          className="icon-btn"
                          title="تبديل الظهور"
                          onClick={() => void toggleVisibility(p)}
                        >
                          <Icon name="check" size={16} />
                        </button>
                        <button type="button" className="icon-btn danger" title="حذف" onClick={() => void remove(p.id)}>
                          <Icon name="close" size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </ListTable>
        )}
      </SectionCard>

      {productForm ? (
        <ProductEditorModal
          open={editorOpen}
          form={productForm}
          categories={categories}
          providers={providers}
          onClose={() => setEditorOpen(false)}
          onChange={setProductForm}
          onSaved={() => state.reload()}
        />
      ) : null}

      <Modal
        open={relinkOpen}
        title="تغيير ربط المنتج"
        subtitle="حدد المصدر ورقم المنتج الجديد."
        onClose={() => setRelinkOpen(false)}
      >
        <Form onSubmit={() => void runRelink()}>
          <Select
            label="المصدر"
            value={relinkProviderId}
            onChange={setRelinkProviderId}
            options={apiProviders.map((pr) => ({
              value: str(pr.id),
              label: str(pr.provider_name, str(pr.id)),
            }))}
          />
          <Input
            label="رقم المنتج في المصدر"
            value={relinkExternalId}
            onChange={(e) => setRelinkExternalId(e.target.value.replace(/[^0-9]/g, ''))}
            inputMode="numeric"
            required
          />
          <div className="modal-actions">
            <Button type="button" variant="secondary" onClick={() => setRelinkOpen(false)}>
              إلغاء
            </Button>
            <Button type="submit">حفظ الربط</Button>
          </div>
        </Form>
      </Modal>
    </ScreenFrame>
  );
}

type CategoryForm = {
  id: string;
  name: string;
  category_kind: string;
  parent_id: string;
  image_url: string;
  sort_order: string;
  is_active: string;
};

const EMPTY_CATEGORY: CategoryForm = {
  id: '',
  name: '',
  category_kind: 'main',
  parent_id: '',
  image_url: '',
  sort_order: '0',
  is_active: '1',
};

export function CategoriesScreen() {
  const state = useProductsCatalog();
  const [q, setQ] = useState('');
  const [providerFilter, setProviderFilter] = useState('all');
  const [kindFilter, setKindFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [form, setForm] = useState<CategoryForm>({ ...EMPTY_CATEGORY });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const categories = state.data?.categories ?? [];
  const products = state.data?.products ?? [];
  const providers = state.data?.providers ?? [];
  const mainCategories = useMemo(
    () => categories.filter((c) => categoryKind(c) === 'main'),
    [categories],
  );

  const mainCount = useMemo(
    () => categories.filter((c) => categoryKind(c) === 'main').length,
    [categories],
  );
  const subCount = useMemo(
    () => categories.filter((c) => categoryKind(c) === 'sub').length,
    [categories],
  );

  function productsInCategory(categoryId: unknown): number {
    return products.filter((p) => Number(p.category_id ?? 0) === Number(categoryId)).length;
  }

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return categories.filter((c) => {
      const kind = categoryKind(c);
      if (kindFilter === 'main' && kind !== 'main') return false;
      if (kindFilter === 'sub' && kind !== 'sub') return false;
      if (providerFilter !== 'all' && str(c.provider_id ?? '0') !== providerFilter) return false;
      const active = Number(c.is_active ?? 1) === 1;
      if (statusFilter === 'active' && !active) return false;
      if (statusFilter === 'hidden' && active) return false;
      if (!needle) return true;
      return [str(c.name), str(c.parent_name), categoryPath(c), str(c.provider_name)]
        .join(' ')
        .toLowerCase()
        .includes(needle);
    });
  }, [categories, q, providerFilter, kindFilter, statusFilter]);

  async function save() {
    const miss = required({ name: form.name }, ['name']);
    if (miss) {
      setMsg(miss);
      return;
    }
    if (form.category_kind === 'sub' && !form.parent_id) {
      setMsg('اختر القسم الأب للقسم الفرعي.');
      return;
    }
    setBusy(true);
    const body: JsonMap = {
      name: form.name,
      category_kind: form.category_kind,
      parent_id: form.category_kind === 'sub' ? Number(form.parent_id) : 0,
      image_url: form.image_url,
      sort_order: Number(form.sort_order) || 0,
      is_active: form.is_active === '1' ? 1 : 0,
    };
    if (form.id) body.id = form.id;
    const res = await admin.categorySave(body);
    setBusy(false);
    setMsg(res.success ? 'تم حفظ القسم.' : errorMessage(res));
    if (res.success) {
      setForm({ ...EMPTY_CATEGORY });
      state.reload();
    }
  }

  function editCategory(c: JsonMap) {
    setForm({
      id: str(c.id),
      name: str(c.name),
      category_kind: str(c.category_kind, Number(c.parent_id) > 0 ? 'sub' : 'main'),
      parent_id: str(c.parent_id ?? ''),
      image_url: str(c.image_url),
      sort_order: str(c.sort_order ?? '0'),
      is_active: Number(c.is_active ?? 1) === 1 ? '1' : '0',
    });
    setMsg('');
  }

  async function remove(id: unknown) {
    if (
      !window.confirm(
        'حذف القسم من العرض؟ سيتم إخفاء الأقسام الفرعية والمنتجات داخله مع الحفاظ على الطلبات القديمة.',
      )
    ) {
      return;
    }
    const res = await admin.categoryDelete({ id });
    if (!res.success) {
      window.alert(errorMessage(res));
      return;
    }
    state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="orders"
      title="إدارة الأقسام"
      subtitle="أنشئ الأقسام الرئيسية والفرعية وحدد القسم الأب عند الحاجة."
      actions={
        <>
          <Button type="button" variant="secondary" onClick={() => state.reload()}>
            <Icon name="refresh" size={18} /> تحديث
          </Button>
          <Button type="button" onClick={() => setForm({ ...EMPTY_CATEGORY })}>
            <Icon name="package" size={18} /> قسم جديد
          </Button>
        </>
      }
    >
      <MetricGrid>
        <MetricCard title="رئيسية" value={String(mainCount)} icon={<Icon name="game" size={21} />} />
        <MetricCard title="فرعية" value={String(subCount)} icon={<Icon name="orders" size={21} />} />
        <MetricCard title="منتجات" value={String(products.length)} icon={<Icon name="package" size={21} />} />
      </MetricGrid>

      <SectionCard
        title={form.id ? 'تعديل قسم' : 'إضافة قسم'}
        subtitle="اختر النوع: رئيسي يظهر بالواجهة، أو فرعي يظهر داخل قسم أب."
        icon={<Icon name="package" size={24} />}
      >
        <Form className="form-grid two-columns" onSubmit={() => void save()}>
          <Input label="اسم القسم" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          <Select
            label="نوع القسم"
            value={form.category_kind}
            onChange={(v) => setForm({ ...form, category_kind: v, parent_id: v === 'main' ? '' : form.parent_id })}
            options={[
              { value: 'main', label: 'رئيسي' },
              { value: 'sub', label: 'فرعي داخل قسم' },
            ]}
          />
          {form.category_kind === 'sub' ? (
            <Select
              label="القسم الأب"
              value={form.parent_id}
              onChange={(v) => setForm({ ...form, parent_id: v })}
              options={mainCategories.map((c) => ({ value: str(c.id), label: str(c.name) }))}
              required
            />
          ) : null}
          <Input
            label="الترتيب"
            value={form.sort_order}
            onChange={(e) => setForm({ ...form, sort_order: e.target.value })}
            inputMode="numeric"
          />
          <Select
            label="الحالة"
            value={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: v })}
            options={[
              { value: '1', label: 'ظاهر' },
              { value: '0', label: 'مخفي' },
            ]}
          />
          <ImagePicker
            label="صورة القسم"
            value={form.image_url}
            onChange={(url) => setForm({ ...form, image_url: url })}
            onNotify={(m) => setMsg(m)}
          />
          <FlashMessage message={msg} />
          <FormActions>
            <Button type="submit" disabled={busy}>
              {form.id ? 'حفظ القسم' : 'إضافة قسم'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setForm({ ...EMPTY_CATEGORY })}>
              {form.id ? 'إلغاء التعديل' : 'تفريغ الحقول'}
            </Button>
          </FormActions>
        </Form>
      </SectionCard>

      <FilterCard
        title="تصفية الأقسام"
        subtitle="حدد النوع والمصدر والحالة لعرض النتائج."
        icon={<Icon name="settings" size={24} />}
      >
        <ProSearch value={q} onChange={setQ} placeholder="ابحث باسم القسم أو القسم الأب" />
        <Select
          label="نوع القسم"
          value={kindFilter}
          onChange={setKindFilter}
          options={[
            { value: 'all', label: 'رئيسي + فرعي' },
            { value: 'main', label: 'رئيسي فقط' },
            { value: 'sub', label: 'فرعي فقط' },
          ]}
        />
        <Select
          label="المصدر"
          value={providerFilter}
          onChange={setProviderFilter}
          options={[
            { value: 'all', label: 'كل المصادر' },
            ...providers.map((pr) => ({
              value: str(pr.id),
              label: str(pr.provider_name, str(pr.id)),
            })),
          ]}
        />
        <Select
          label="الحالة"
          value={statusFilter}
          onChange={setStatusFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'active', label: 'الظاهرة' },
            { value: 'hidden', label: 'المخفية' },
          ]}
        />
      </FilterCard>

      <SectionCard
        title={`كل الأقسام (${filtered.length})`}
        subtitle="القسم الفرعي يظهر داخل الأب في تطبيق الزبون."
        icon={<Icon name="orders" size={24} />}
      >
        {filtered.length === 0 ? (
          <Empty title="لا أقسام مطابقة" hint="عدّل معايير التصفية أو أضف قسماً جديداً." />
        ) : (
          <ListTable tableClassName="pro-table">
            <thead>
              <tr>
                <th>الصورة</th>
                <th>القسم</th>
                <th>النوع</th>
                <th>الأب</th>
                <th>المصدر</th>
                <th>منتجات</th>
                <th>ترتيب</th>
                <th>الحالة</th>
                <th>إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => (
                <tr key={str(c.id)}>
                  <td>
                    <TableThumb src={c.image_url} label={str(c.name)} />
                  </td>
                  <td>
                    <strong>{str(c.name)}</strong>
                    <small>#{str(c.id)}</small>
                  </td>
                  <td>{categoryKind(c) === 'sub' ? 'فرعي' : 'رئيسي'}</td>
                  <td>{str(c.parent_name, '—')}</td>
                  <td>{str(c.provider_name, 'منتجات يدوية')}</td>
                  <td>{String(productsInCategory(c.id))}</td>
                  <td>{str(c.sort_order ?? '0')}</td>
                  <td>
                    <StatusBadge status={Number(c.is_active ?? 1) === 1 ? 'active' : 'blocked'} />
                  </td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="icon-btn" title="تعديل" onClick={() => editCategory(c)}>
                        <Icon name="edit" size={16} />
                      </button>
                      <button type="button" className="icon-btn danger" title="حذف" onClick={() => void remove(c.id)}>
                        <Icon name="close" size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </ListTable>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}

export function OrderingScreen() {
  const state = useProductsCatalog();
  const [categoryId, setCategoryId] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const categories = state.data?.categories ?? [];
  const products = state.data?.products ?? [];

  useEffect(() => {
    const first = categories[0];
    if (!categoryId && first) {
      setCategoryId(str(first.id));
    }
  }, [categories, categoryId]);

  const sortedCategories = useMemo(
    () => [...categories].sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0)),
    [categories],
  );

  const categoryProducts = useMemo(() => {
    const list = products.filter((p) => {
      if (!categoryId) return false;
      return str(p.category_id) === categoryId;
    });
    return [...list].sort((a, b) => Number(a.sort_order ?? 0) - Number(b.sort_order ?? 0));
  }, [products, categoryId]);

  async function saveCategoryOrders() {
    setBusy(true);
    let ok = true;
    for (const c of sortedCategories) {
      const res = await admin.categorySave({
        id: c.id,
        name: c.name,
        category_kind: c.category_kind,
        parent_id: c.parent_id,
        image_url: c.image_url,
        sort_order: c.sort_order,
        is_active: c.is_active,
      });
      if (!res.success) ok = false;
    }
    setBusy(false);
    setMsg(ok ? 'تم حفظ ترتيب الأقسام.' : 'بعض الأقسام لم تُحفظ.');
    if (ok) state.reload();
  }

  async function bumpCategory(c: JsonMap, delta: number) {
    const sort = Number(c.sort_order ?? 0) + delta;
    const res = await admin.categorySave({
      id: c.id,
      name: c.name,
      category_kind: c.category_kind,
      parent_id: c.parent_id,
      image_url: c.image_url,
      sort_order: sort,
      is_active: c.is_active,
    });
    setMsg(res.success ? 'تم تحديث الترتيب.' : errorMessage(res));
    if (res.success) state.reload();
  }

  async function saveProductOrders() {
    setBusy(true);
    let ok = true;
    for (const p of categoryProducts) {
      const res = await admin.productSave({
        id: p.id,
        sort_order: p.sort_order,
        name: p.name,
      });
      if (!res.success) ok = false;
    }
    setBusy(false);
    setMsg(ok ? 'تم حفظ ترتيب المنتجات.' : 'بعض المنتجات لم تُحفظ.');
    if (ok) state.reload();
  }

  async function bumpProduct(p: JsonMap, delta: number) {
    const sort = Number(p.sort_order ?? 0) + delta;
    const res = await admin.productSave({
      id: p.id,
      sort_order: sort,
      name: p.name,
    });
    setMsg(res.success ? 'تم تحديث الترتيب.' : errorMessage(res));
    if (res.success) state.reload();
  }

  if (state.loading) return <Loading />;
  if (state.error) return <ErrorBox message={state.error} onRetry={state.reload} />;

  return (
    <ScreenFrame
      mode="admin"
      icon="orders"
      title="ترتيب العرض"
      subtitle="رتّب الأقسام والمنتجات بأزرار صعود/نزول أو اكتب رقم الترتيب يدويًا، ثم احفظ."
      actions={
        <Button type="button" variant="secondary" onClick={() => state.reload()}>
          <Icon name="refresh" size={18} /> تحديث
        </Button>
      }
    >
      <FlashMessage message={msg} />

      <section className="pro-grid two ordering-layout">
        <SectionCard
          title="ترتيب الأقسام"
          subtitle="الأرقام الأقل تظهر أولاً."
          icon={<Icon name="orders" size={24} />}
        >
          <div className="action-row wrap">
            <Button type="button" disabled={busy} onClick={() => void saveCategoryOrders()}>
              حفظ ترتيب الأقسام
            </Button>
          </div>
          {sortedCategories.length === 0 ? (
            <Empty title="لا أقسام" />
          ) : (
            <div className="ordering-list">
              {sortedCategories.map((c) => (
                <article key={str(c.id)} className="ordering-row">
                  <TableThumb src={c.image_url} label={categoryPath(c)} />
                  <div>
                    <strong>
                      {str(c.sort_order ?? 0)}. {categoryPath(c)}
                    </strong>
                    <small className="muted">{categoryKind(c) === 'sub' ? 'فرعي' : 'رئيسي'}</small>
                  </div>
                  <div className="table-actions">
                    <button type="button" className="icon-btn" title="أعلى" onClick={() => void bumpCategory(c, -1)}>
                      ↑
                    </button>
                    <button type="button" className="icon-btn" title="أسفل" onClick={() => void bumpCategory(c, 1)}>
                      ↓
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </SectionCard>

        <SectionCard
          title="ترتيب المنتجات"
          subtitle="اختر قسماً ثم رتّب منتجاته."
          icon={<Icon name="package" size={24} />}
        >
          <Select
            label="القسم"
            value={categoryId}
            onChange={setCategoryId}
            options={categories.map((c) => ({
              value: str(c.id),
              label: categoryPath(c),
            }))}
          />
          <div className="action-row wrap">
            <Button type="button" disabled={busy} onClick={() => void saveProductOrders()}>
              حفظ ترتيب المنتجات
            </Button>
          </div>
          {categoryProducts.length === 0 ? (
            <Empty title="لا منتجات للترتيب" />
          ) : (
            <div className="ordering-list">
              {categoryProducts.map((p) => (
                <article key={str(p.id)} className="ordering-row product">
                  <TableThumb src={productImageSrc(p)} label={str(p.name)} />
                  <div>
                    <strong>
                      {str(p.sort_order ?? 0)}. {str(p.name)}
                    </strong>
                  </div>
                  <div className="table-actions">
                    <button type="button" className="icon-btn" title="أعلى" onClick={() => void bumpProduct(p, -1)}>
                      ↑
                    </button>
                    <button type="button" className="icon-btn" title="أسفل" onClick={() => void bumpProduct(p, 1)}>
                      ↓
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
        </SectionCard>
      </section>
    </ScreenFrame>
  );
}

export function ImportScreen() {
  const settings = useAsync(
    () => admin.productsSettings(),
    (p) => {
      const data = p.data && typeof p.data === 'object' ? (p.data as JsonMap) : (p as JsonMap);
      const providers = Array.isArray(data.providers) ? (data.providers as JsonMap[]) : [];
      const defaultProvider =
        data.default_provider && typeof data.default_provider === 'object'
          ? (data.default_provider as JsonMap)
          : providers[0] ?? null;
      return { providers, defaultProvider };
    },
  );

  const [providerId, setProviderId] = useState('');
  const [catalog, setCatalog] = useState<JsonMap[]>([]);
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const [q, setQ] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [availFilter, setAvailFilter] = useState('all');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  const activeProviderId =
    providerId ||
    (settings.data?.defaultProvider?.id != null && settings.data.defaultProvider.id !== ''
      ? String(settings.data.defaultProvider.id)
      : '');

  const categoryOptions = useMemo(() => {
    const names = new Set<string>();
    for (const item of catalog) {
      const name = str(item.category_name, '').trim();
      if (name) names.add(name);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b, 'ar'));
  }, [catalog]);

  const filteredCatalog = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return catalog.filter((item) => {
      const itemCategory = str(item.category_name, '').trim();
      if (categoryFilter !== 'all' && itemCategory !== categoryFilter) return false;
      const rawAvail = item.available;
      const available =
        rawAvail === false || rawAvail === 0 || rawAvail === '0'
          ? false
          : rawAvail === true || rawAvail === 1 || rawAvail === '1' || rawAvail == null || rawAvail === ''
            ? true
            : Number(rawAvail) === 1;
      if (availFilter === 'available' && !available) return false;
      if (availFilter === 'unavailable' && available) return false;
      if (!needle) return true;
      const hay = [str(item.name, ''), itemCategory, str(item.id, '')].join(' ').toLowerCase();
      return hay.includes(needle);
    });
  }, [availFilter, catalog, categoryFilter, q]);

  const selectedIds = useMemo(
    () =>
      Object.entries(selected)
        .filter(([, v]) => v)
        .map(([id]) => Number(id)),
    [selected],
  );

  const allVisibleSelected =
    filteredCatalog.length > 0 && filteredCatalog.every((item) => selected[str(item.id)]);

  function setVisibleSelection(checked: boolean) {
    setSelected((prev) => {
      const next = { ...prev };
      for (const item of filteredCatalog) {
        next[str(item.id)] = checked;
      }
      return next;
    });
  }

  const fetchCatalog = useCallback(async () => {
    if (!activeProviderId) {
      setMsg('اختر مصدر API أولاً.');
      return;
    }
    setBusy(true);
    const res = await apiGet<JsonMap>(`/admin/products/external?provider_id=${encodeURIComponent(activeProviderId)}`);
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    const items = pickList(res, 'data', 'items', 'products');
    setCatalog(items);
    setSelected({});
    setCategoryFilter('all');
    setAvailFilter('all');
    setQ('');
    setMsg('تم جلب المنتجات من المصدر.');
  }, [activeProviderId]);

  async function importSelected(all = false) {
    if (!activeProviderId) return;
    setBusy(true);
    const body: JsonMap = { provider_id: Number(activeProviderId) };
    if (all) body.import_all = 1;
    else body.product_ids = selectedIds;
    const res = await admin.productsImport(body);
    setBusy(false);
    if (!res.success) {
      setMsg(errorMessage(res));
      return;
    }
    const imported = Number(res.count ?? selectedIds.length) || 0;
    setMsg(
      str(
        res.message,
        imported === 1 ? 'تم استيراد المنتج بنجاح.' : 'تم استيراد المنتجات بنجاح.',
      ) + ' راجعها من شاشة المنتجات؛ فعّل الظاهر وعيّن السعر إن لزم.',
    );
    settings.reload();
  }

  if (settings.loading) return <Loading />;
  if (settings.error) return <ErrorBox message={settings.error} onRetry={settings.reload} />;

  const providers = settings.data?.providers ?? [];

  return (
    <ScreenFrame
      mode="admin"
      icon="settings"
      title="استيراد منتجات API"
      subtitle="حمّل كتالوج المصدر، ثم حدد المنتجات المطلوب استيرادها."
      actions={
        <Button type="button" variant="secondary" onClick={() => settings.reload()}>
          <Icon name="refresh" size={18} /> تحديث المصادر
        </Button>
      }
    >
      <FlashMessage message={msg} />

      <FilterCard
        title="تصفية الكتالوج"
        subtitle="اختر المصدر وجِب المنتجات، ثم صفِّ النتائج قبل الاستيراد."
        icon={<Icon name="search" size={24} />}
      >
        <Select
          label="المصدر"
          value={activeProviderId}
          onChange={(v) => {
            setProviderId(v);
            setCatalog([]);
            setSelected({});
            setCategoryFilter('all');
            setAvailFilter('all');
            setQ('');
          }}
          options={providers
            .filter((pr) => str(pr.provider_type) !== 'manual')
            .map((pr) => ({
              value: str(pr.id),
              label: str(pr.provider_name, str(pr.id)),
            }))}
        />
        <ProSearch value={q} onChange={setQ} placeholder="بحث باسم المنتج أو القسم أو المعرف" />
        <Select
          label="القسم"
          value={categoryFilter}
          onChange={setCategoryFilter}
          options={[
            { value: 'all', label: 'كل الأقسام' },
            ...categoryOptions.map((name) => ({ value: name, label: name })),
          ]}
        />
        <Select
          label="التوفر"
          value={availFilter}
          onChange={setAvailFilter}
          options={[
            { value: 'all', label: 'الكل' },
            { value: 'available', label: 'متاح' },
            { value: 'unavailable', label: 'غير متاح' },
          ]}
        />
        <Button type="button" disabled={busy || !activeProviderId} onClick={() => void fetchCatalog()}>
          <Icon name="refresh" size={18} /> جلب من API
        </Button>
      </FilterCard>

      <SectionCard
        title={`منتجات المصدر (${filteredCatalog.length})`}
        subtitle={
          selectedIds.length
            ? `${selectedIds.length} منتج محدد للاستيراد`
            : 'استخدم خانة التحديد أو الأزرار أدناه ثم استورد.'
        }
        icon={<Icon name="package" size={24} />}
        actions={
          catalog.length ? (
            <>
              <Button
                type="button"
                disabled={busy || !selectedIds.length}
                onClick={() => void importSelected(false)}
              >
                استيراد المحدد ({selectedIds.length})
              </Button>
              <Button type="button" variant="secondary" disabled={busy} onClick={() => void importSelected(true)}>
                استيراد الكل
              </Button>
            </>
          ) : null
        }
      >
        {!catalog.length ? (
          <Empty title="لا توجد منتجات" hint="اختر المصدر ثم اضغط «جلب من API»." />
        ) : filteredCatalog.length === 0 ? (
          <Empty title="لا منتجات مطابقة" hint="عدّل البحث أو فلاتر القسم والتوفر." />
        ) : (
          <>
            <div className="btn-row import-toolbar">
              <Button type="button" variant="secondary" onClick={() => setVisibleSelection(true)}>
                تحديد الظاهر
              </Button>
              <Button type="button" variant="secondary" onClick={() => setVisibleSelection(false)}>
                إلغاء التحديد
              </Button>
            </div>
            <ListTable tableClassName="import-products-table">
              <thead>
                <tr>
                  <th className="col-check">
                    <input
                      type="checkbox"
                      aria-label="تحديد الظاهر"
                      checked={allVisibleSelected}
                      onChange={(e) => setVisibleSelection(e.target.checked)}
                    />
                  </th>
                  <th>المنتج</th>
                  <th>القسم</th>
                  <th>النوع</th>
                  <th>السعر</th>
                  <th>التوفر</th>
                </tr>
              </thead>
              <tbody>
                {filteredCatalog.map((item) => {
                  const id = str(item.id);
                  const checked = Boolean(selected[id]);
                  const rawAvail = item.available;
                  const available =
                    rawAvail === false || rawAvail === 0 || rawAvail === '0'
                      ? false
                      : rawAvail === true ||
                          rawAvail === 1 ||
                          rawAvail === '1' ||
                          rawAvail == null ||
                          rawAvail === ''
                        ? true
                        : Number(rawAvail) === 1;
                  const typeKey = str(item.product_type, '');
                  return (
                    <tr key={id} className={checked ? 'is-selected' : undefined}>
                      <td className="col-check">
                        <input
                          type="checkbox"
                          aria-label={`تحديد ${str(item.name, id)}`}
                          checked={checked}
                          onChange={(e) => setSelected({ ...selected, [id]: e.target.checked })}
                        />
                      </td>
                      <td>
                        <div className="cell-stack">
                          <strong>{str(item.name)}</strong>
                          <small>#{id}</small>
                        </div>
                      </td>
                      <td>{str(item.category_name, '—')}</td>
                      <td>{PRODUCT_TYPE_LABELS[typeKey] ?? (typeKey || '—')}</td>
                      <td>
                        <Money value={item.price ?? item.provider_price} suffix="$" />
                      </td>
                      <td>
                        <span className={`status-pill ${available ? 'active' : 'inactive'}`}>
                          {available ? 'متاح' : 'غير متاح'}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </ListTable>
          </>
        )}
      </SectionCard>
    </ScreenFrame>
  );
}

type ProviderForm = {
  id: string;
  provider_name: string;
  provider_type: string;
  base_url: string;
  api_token: string;
  usd_rate: string;
  is_active: string;
};

export function SourcesScreen() {
  const settings = useAsync(
    () => admin.productsSettings(),
    (p) => {
      const data = p.data && typeof p.data === 'object' ? (p.data as JsonMap) : (p as JsonMap);
      const providers = Array.isArray(data.providers) ? (data.providers as JsonMap[]) : [];
      return providers;
    },
  );

  const [form, setForm] = useState<ProviderForm>({
    id: '',
    provider_name: 'مصدر منتجات',
    provider_type: 'generic',
    base_url: '',
    api_token: '',
    usd_rate: '1',
    is_active: '1',
  });
  const [msg, setMsg] = useState('');
  const [profileMsg, setProfileMsg] = useState('');
  const [busy, setBusy] = useState(false);

  function loadProvider(p: JsonMap) {
    setForm({
      id: str(p.id),
      provider_name: str(p.provider_name, 'مصدر منتجات'),
      provider_type: str(p.provider_type, 'generic'),
      base_url: str(p.base_url),
      api_token: '',
      usd_rate: str(p.usd_rate ?? '1'),
      is_active: Number(p.is_active ?? 1) === 1 ? '1' : '0',
    });
    setMsg('');
    setProfileMsg('');
  }

  async function saveProvider() {
    setBusy(true);
    const body: JsonMap = {
      provider_id: form.id ? Number(form.id) : undefined,
      id: form.id ? Number(form.id) : undefined,
      provider_name: form.provider_name,
      provider_type: form.provider_type,
      base_url: form.base_url,
      usd_rate: Number(form.usd_rate) || 1,
      is_active: form.is_active === '1' ? 1 : 0,
    };
    if (form.api_token.trim()) body.api_token = form.api_token.trim();
    const res = await admin.saveProductsSettings(body);
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تم حفظ المصدر.') : errorMessage(res));
    if (res.success) settings.reload();
  }

  async function checkProfile(providerId?: string) {
    const id = providerId ?? form.id;
    if (!id) {
      setProfileMsg('احفظ المصدر أولاً.');
      return;
    }
    setBusy(true);
    const res = await admin.productsProfile({ provider_id: id });
    setBusy(false);
    const okMsg = 'تم فحص المصدر بنجاح.';
    if (providerId) {
      setMsg(res.success ? okMsg : errorMessage(res));
    } else {
      setProfileMsg(res.success ? okMsg : errorMessage(res));
    }
    if (res.success) settings.reload();
  }

  async function syncPricesForProvider(providerId: string) {
    setBusy(true);
    const res = await apiPost('/admin/products/sync-prices', { provider_id: Number(providerId) });
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تمت مزامنة الأسعار.') : errorMessage(res));
    if (res.success) settings.reload();
  }

  async function syncPrices() {
    setBusy(true);
    const res = await apiPost(
      '/admin/products/sync-prices',
      form.id ? { provider_id: Number(form.id) } : {},
    );
    setBusy(false);
    setMsg(res.success ? str(res.message, 'تمت مزامنة الأسعار.') : errorMessage(res));
  }

  if (settings.loading) return <Loading />;
  if (settings.error) return <ErrorBox message={settings.error} onRetry={settings.reload} />;

  const providers = settings.data ?? [];
  const apiProviders = providers.filter((p) => str(p.provider_type) !== 'manual');

  return (
    <ScreenFrame
      mode="admin"
      icon="settings"
      title="مصادر المنتجات"
      subtitle="أضف مصادر API واضبط رمز الوصول ورابط المصدر وسعر الصرف."
      actions={
        <Button
          type="button"
          onClick={() =>
            setForm({
              id: '',
              provider_name: 'مصدر جديد',
              provider_type: 'generic',
              base_url: '',
              api_token: '',
              usd_rate: '1',
              is_active: '1',
            })
          }
        >
          <Icon name="package" size={18} /> إضافة مصدر
        </Button>
      }
    >
      <SectionCard title={form.id ? 'تعديل المصدر' : 'مصدر جديد'} icon={<Icon name="wallet" size={24} />}>
        <Form className="form-grid two-columns" onSubmit={() => void saveProvider()}>
          <Input
            label="اسم المصدر"
            value={form.provider_name}
            onChange={(e) => setForm({ ...form, provider_name: e.target.value })}
            required
          />
          <Select
            label="نوع المصدر"
            value={form.provider_type}
            onChange={(v) => {
              const opt = PROVIDER_TYPE_OPTIONS.find((o) => o.value === v);
              setForm((prev) => ({
                ...prev,
                provider_type: v,
                base_url:
                  !prev.base_url.trim() ||
                  PROVIDER_TYPE_OPTIONS.some((o) => o.baseUrl === prev.base_url.trim())
                    ? (opt?.baseUrl ?? prev.base_url)
                    : prev.base_url,
              }));
            }}
            options={PROVIDER_TYPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
          />
          <Input
            className="span-2"
            label="الرابط الأساسي"
            value={form.base_url}
            onChange={(e) => setForm({ ...form, base_url: e.target.value })}
          />
          <Input
            className="span-2"
            label="رمز الوصول (اتركه فارغاً للإبقاء على الموجود)"
            value={form.api_token}
            onChange={(e) => setForm({ ...form, api_token: e.target.value })}
            placeholder={form.id ? 'موجود / مخفي' : 'ضع التوكن'}
          />
          <Input
            label="سعر الدولار/الليرة"
            value={form.usd_rate}
            onChange={(e) => setForm({ ...form, usd_rate: e.target.value })}
            inputMode="decimal"
          />
          <Select
            label="الحالة"
            value={form.is_active}
            onChange={(v) => setForm({ ...form, is_active: v })}
            options={[
              { value: '1', label: 'فعال' },
              { value: '0', label: 'معطل' },
            ]}
          />
          <FormActions>
            <Button type="submit" disabled={busy}>
              حفظ المصدر
            </Button>
            <Button type="button" variant="secondary" disabled={busy || !form.id} onClick={() => void checkProfile()}>
              فحص المصدر
            </Button>
            <Button type="button" variant="secondary" disabled={busy} onClick={() => void syncPrices()}>
              مزامنة الأسعار
            </Button>
          </FormActions>
          {profileMsg ? <p className={profileMsg.includes('تم') ? 'ok' : 'error'}>{profileMsg}</p> : null}
          <FlashMessage message={msg} />
        </Form>
        {form.base_url ? (
          <div className="api-connection-grid top-space">
            <CopyField label="الرابط الأساسي" value={form.base_url} badge="HTTPS" />
          </div>
        ) : null}
      </SectionCard>

      <SectionCard title="المصادر الحالية" icon={<Icon name="settings" size={24} />}>
          {apiProviders.length === 0 ? (
            <Empty title="لا مصادر API" hint="أضف مصدراً واربط التوكن." />
          ) : (
            <div className="stack-gap">
              {apiProviders.map((p) => (
                <article key={str(p.id)} className="provider-card">
                  <div>
                    <strong>{str(p.provider_name)}</strong>
                    <small>
                      {PROVIDER_TYPE_OPTIONS.find((o) => o.value === str(p.provider_type))?.label ??
                        str(p.provider_type)}{' '}
                      · {str(p.base_url, '—')}
                    </small>
                  </div>
                  <div className="provider-meta">
                    <StatusBadge status={Number(p.is_active ?? 1) === 1 ? 'active' : 'blocked'} />
                    <span>توكن: {Boolean(p.has_token) ? 'موجود' : 'غير مضبوط'}</span>
                    <span>الرصيد: {str(p.last_balance, '—')}</span>
                  </div>
                  <div className="table-actions">
                    <button type="button" className="icon-btn" title="تعديل" onClick={() => loadProvider(p)}>
                      <Icon name="edit" size={16} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="فحص المصدر"
                      disabled={busy}
                      onClick={() => void checkProfile(str(p.id))}
                    >
                      <Icon name="search" size={16} />
                    </button>
                    <button
                      type="button"
                      className="icon-btn"
                      title="مزامنة الأسعار"
                      disabled={busy}
                      onClick={() => void syncPricesForProvider(str(p.id))}
                    >
                      <Icon name="refresh" size={16} />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          )}
      </SectionCard>
    </ScreenFrame>
  );
}
